import { useState, FormEvent } from 'react';

export default function ContactForm() {
  const [formData, setFormData] = useState({
    coachName: '',
    schoolName: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d4g5noneq0hdrsdob690', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          coachName: '',
          schoolName: '',
          email: '',
          phone: '',
          message: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-20 px-4 bg-gradient-to-br from-gray-900 to-gray-800 relative overflow-hidden">
      <div className="absolute top-0 left-0 w-full h-full opacity-10">
        <div className="absolute top-20 right-20 w-96 h-96 bg-orange-500 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 left-20 w-96 h-96 bg-red-500 rounded-full blur-3xl"></div>
      </div>
      
      <div className="max-w-5xl mx-auto relative z-10">
        <div className="text-center mb-16">
          <h2 className="text-5xl md:text-6xl font-black text-white mb-4">
            GET IN TOUCH
          </h2>
          <div className="w-24 h-2 bg-gradient-to-r from-orange-500 to-red-500 mx-auto rounded-full mb-4"></div>
          <p className="text-xl text-gray-300 max-w-2xl mx-auto">
            Interested in recruiting me? Let's connect and discuss how I can contribute to your program
          </p>
        </div>
        
        <div className="grid lg:grid-cols-2 gap-12 items-start">
          <div className="space-y-8">
            <div className="bg-gradient-to-br from-orange-500 to-red-500 rounded-3xl p-8 text-white">
              <h3 className="text-3xl font-black mb-6">WHY RECRUIT ME?</h3>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-2xl flex-shrink-0 mt-1"></i>
                  <span className="text-lg">Proven track record of athletic excellence and leadership</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-2xl flex-shrink-0 mt-1"></i>
                  <span className="text-lg">Strong academic performance with 3.4 GPA</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-2xl flex-shrink-0 mt-1"></i>
                  <span className="text-lg">Elite defensive libero with exceptional court awareness</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-2xl flex-shrink-0 mt-1"></i>
                  <span className="text-lg">Coachable attitude and team-first mentality</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-2xl flex-shrink-0 mt-1"></i>
                  <span className="text-lg">Committed to continuous improvement and growth</span>
                </li>
              </ul>
            </div>
            
            <div className="bg-white/10 backdrop-blur-sm rounded-3xl p-8 text-white">
              <h3 className="text-2xl font-bold mb-6 flex items-center gap-3">
                <i className="ri-information-fill text-orange-500 text-3xl"></i>
                Quick Info
              </h3>
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <i className="ri-calendar-fill text-orange-500 text-xl"></i>
                  <span>Graduation: Class of 2028</span>
                </div>
                <div className="flex items-center gap-3">
                  <i className="ri-map-pin-fill text-orange-500 text-xl"></i>
                  <span>Location: Available for discussion</span>
                </div>
                <div className="flex items-center gap-3">
                  <i className="ri-time-fill text-orange-500 text-xl"></i>
                  <span>Available: Year-round for camps &amp; visits</span>
                </div>
              </div>
            </div>
          </div>
          
          <form 
            id="coach-contact-form"
            data-readdy-form
            onSubmit={handleSubmit}
            className="bg-white rounded-3xl p-8 shadow-2xl"
          >
            <h3 className="text-3xl font-black text-gray-900 mb-6">Contact Form</h3>
            
            <div className="space-y-5">
              <div>
                <label htmlFor="coachName" className="block text-sm font-semibold text-gray-700 mb-2">
                  Coach Name *
                </label>
                <input
                  type="text"
                  id="coachName"
                  name="coachName"
                  required
                  value={formData.coachName}
                  onChange={(e) => setFormData({...formData, coachName: e.target.value})}
                  className="w-full px-4 py-3 text-sm border-2 border-gray-200 rounded-xl focus:border-orange-500 focus:outline-none transition-colors"
                  placeholder="Your name"
                />
              </div>
              
              <div>
                <label htmlFor="schoolName" className="block text-sm font-semibold text-gray-700 mb-2">
                  School/Program Name *
                </label>
                <input
                  type="text"
                  id="schoolName"
                  name="schoolName"
                  required
                  value={formData.schoolName}
                  onChange={(e) => setFormData({...formData, schoolName: e.target.value})}
                  className="w-full px-4 py-3 text-sm border-2 border-gray-200 rounded-xl focus:border-orange-500 focus:outline-none transition-colors"
                  placeholder="University or college name"
                />
              </div>
              
              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="w-full px-4 py-3 text-sm border-2 border-gray-200 rounded-xl focus:border-orange-500 focus:outline-none transition-colors"
                  placeholder="coach@university.edu"
                />
              </div>
              
              <div>
                <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">
                  Phone Number
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  className="w-full px-4 py-3 text-sm border-2 border-gray-200 rounded-xl focus:border-orange-500 focus:outline-none transition-colors"
                  placeholder="(555) 123-4567"
                />
              </div>
              
              <div>
                <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">
                  Message *
                </label>
                <textarea
                  id="message"
                  name="message"
                  required
                  rows={5}
                  maxLength={500}
                  value={formData.message}
                  onChange={(e) => setFormData({...formData, message: e.target.value})}
                  className="w-full px-4 py-3 text-sm border-2 border-gray-200 rounded-xl focus:border-orange-500 focus:outline-none transition-colors resize-none"
                  placeholder="Tell me about your program and recruiting timeline..."
                />
                <p className="text-xs text-gray-500 mt-1">{formData.message.length}/500 characters</p>
              </div>
              
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full py-4 bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white font-bold text-lg rounded-xl transition-all duration-300 transform hover:scale-105 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
              >
                {isSubmitting ? 'SENDING...' : 'SEND MESSAGE'}
              </button>
              
              {submitStatus === 'success' && (
                <div className="p-4 bg-green-50 border-2 border-green-500 rounded-xl">
                  <p className="text-green-700 font-semibold text-center">
                    Thank you! I'll get back to you soon.
                  </p>
                </div>
              )}
              
              {submitStatus === 'error' && (
                <div className="p-4 bg-red-50 border-2 border-red-500 rounded-xl">
                  <p className="text-red-700 font-semibold text-center">
                    Something went wrong. Please try again.
                  </p>
                </div>
              )}
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}
